/**
 * useNotificationRealtime — live-syncs the current user's notification inbox.
 *
 * Subscribes to Postgres CDC on `public.notification_user_inbox` filtered to
 * the authenticated user (`user_id=eq.<uid>`) and invalidates the relevant
 * React Query keys on every change.
 *
 * Architecture rules:
 * - UI-only hook. No business logic, no notification creation, no writes.
 * - All mutations continue to flow through the `notifications-write` edge
 *   function. Realtime is purely a cache-sync trigger.
 * - Topic follows the project convention `user:<uid>:notifications`
 *   (enforced by `useRealtimeChannel`).
 * - RLS on `notification_user_inbox` ensures users can only ever receive
 *   their own rows over realtime.
 * - Polling in `useNotifications` / `useUnreadNotificationCount` remains as
 *   a safety net (handled by the central realtime wrapper as polling
 *   fallback after repeated reconnect failures, plus React Query's own
 *   `refetchInterval`).
 */
import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import {
  useRealtimeChannel,
  type RealtimeChannelBinding,
} from "@/hooks/useRealtimeChannel";
import { notificationKeys } from "@/hooks/useNotifications";

export function useNotificationRealtime() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }, [queryClient]);

  const bindings = useMemo<RealtimeChannelBinding[]>(() => {
    if (!userId) return [];
    return [
      {
        postgresChanges: {
          event: "*",
          schema: "public",
          table: "notification_user_inbox",
          filter: `user_id=eq.${userId}`,
        },
        handler: () => invalidate(),
      },
    ];
  }, [userId, invalidate]);

  useRealtimeChannel({
    topic: userId ? `user:${userId}:notifications` : "user:anon:notifications",
    bindings,
    enabled: !!userId,
    // Polling fallback uses the same invalidation path: after the central
    // wrapper exhausts reconnect attempts it will tick this every 30s.
    onPollingTick: invalidate,
    pollingIntervalMs: 30_000,
    onError: (info) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[notifications:realtime]", info);
      }
    },
  });
}